import React, { useRef, useEffect } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined, WarningOutlined } from "@ant-design/icons";
import styles from "./SwmsWizard.module.css";

const QLIS = [
  {
    id: "qli-1",
    title: "T1 – External Window Cleaning",
    areas: ["North Facade", "South Facade", "East Facade", "West Facade"]
  },
  {
    id: "qli-2",
    title: "T2 – Balcony Maintenance",
    areas: ["Level 2 Balconies", "Level 3 Balconies", "Level 4 Balconies", "Penthouse Balcony"]
  },
  {
    id: "qli-3",
    title: "T3 – Roof Access & Plant Room",
    areas: ["Main Rooftop", "HVAC Platform", "Plant Room Access", "Skylight Frames"]
  }
];

const CAT_LABELS = {
  "rope-access": "Rope Access",
  "ewp": "EWP",
  "scaffolding": "Scaffolding",
  "ladder": "Ladder Work",
  "ground": "Ground Level",
  "confined": "Confined Space"
};

export default function SwmsWizardStep3({
  selectedCategories,
  selectedMethods,
  selectedAreas,
  expandedQLIs,
  showValidation,
  onToggleQLIExpand,
  onToggleArea,
  onToggleQLIAllAreas,
  onContinue,
  onBack,
  onCancel
}) {
  const qliCheckboxRefs = useRef({});

  // Update indeterminate state for QLI checkboxes
  useEffect(() => {
    QLIS.forEach((qli) => {
      if (qliCheckboxRefs.current[qli.id]) {
        const selectedAreasInQLI = qli.areas.filter((area) => selectedAreas.includes(area));
        const someAreasSelected = selectedAreasInQLI.length > 0 && selectedAreasInQLI.length < qli.areas.length;
        qliCheckboxRefs.current[qli.id].indeterminate = someAreasSelected;
      }
    });
  }, [selectedAreas]);

  return (
    <div className={styles.stepBody}>
      {/* Recap banner */}
      <div className={styles.recapBanner}>
        <div className={styles.recapColumn}>
          <span className={styles.recapLabel}>Selected Categories:</span>
          <div className={styles.recapTags}>
            {selectedCategories.map((catId) => (
              <span key={catId} className={styles.recapTag}>
                {CAT_LABELS[catId]}
              </span>
            ))}
          </div>
        </div>
        <div className={styles.recapColumn}>
          <span className={styles.recapLabel}>Selected Methods:</span>
          <div className={styles.recapTags}>
            {selectedMethods.slice(0, 3).map((method) => (
              <span key={method} className={styles.recapTag}>
                {method}
              </span>
            ))}
            {selectedMethods.length > 3 && (
              <span className={styles.recapTag}>
                +{selectedMethods.length - 3} more
              </span>
            )}
          </div>
        </div>
      </div>

      <h1 className={styles.stepTitle}>Step 3: Select Quote Line Items</h1>
      <p className={styles.stepSubtitle}>Link this SWMS to specific Quote Line Items and the areas they cover.</p>

      <div className={styles.qlisContainer}>
        <h3 className={styles.qlisTitle}>Job Items & Areas</h3>
        <p className={styles.qlisHint}>Select the Quote Line Items and the specific Areas that this SWMS covers.</p>

        <div className={styles.qlisList}>
          {QLIS.map((qli) => {
            const isExpanded = expandedQLIs.includes(qli.id);
            const selectedAreasInQLI = qli.areas.filter((area) => selectedAreas.includes(area));
            const allAreasSelected = selectedAreasInQLI.length === qli.areas.length;

            return (
              <div key={qli.id} className={styles.qliItem}>
                <div
                  className={styles.qliHeader}
                  onClick={() => onToggleQLIExpand(qli.id)}
                >
                  <input
                    ref={(el) => {
                      if (el) qliCheckboxRefs.current[qli.id] = el;
                    }}
                    type="checkbox"
                    className={styles.qliCheckbox}
                    checked={allAreasSelected}
                    onChange={(e) => onToggleQLIAllAreas(qli.id, e)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className={styles.qliTitle}>{qli.title}</span>
                  <span className={`${styles.qliChevron} ${isExpanded ? styles.expanded : ""}`}>
                    ▼
                  </span>
                </div>

                {isExpanded && (
                  <div className={styles.qliAreas}>
                    {qli.areas.map((area) => {
                      const isAreaSelected = selectedAreas.includes(area);
                      return (
                        <label key={area} className={styles.areaLabel}>
                          <input
                            type="checkbox"
                            className={styles.areaCheckbox}
                            checked={isAreaSelected}
                            onChange={() => onToggleArea(area)}
                          />
                          <span>{area}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {showValidation && (
          <div className={styles.validationMsg}>
            <WarningOutlined />
            Please select at least one area to continue.
          </div>
        )}
      </div>

      <div className={styles.stepFooterBetween}>
        <button className={styles.backButton} onClick={onBack}>
          <ArrowLeftOutlined />
          Back to Methods
        </button>
        <div className={styles.footerRightGroup}>
          <button className={styles.cancelButton} onClick={onCancel}>
            Cancel
          </button>
          <button className={styles.continueButton} onClick={onContinue}>
            Create SWMS
            <ArrowRightOutlined />
          </button>
        </div>
      </div>
    </div>
  );
}
